import React, { useState } from 'react';
import { Clock, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCreateCustomEvent } from '../../api/schedules';
import { useFilterCamps } from '../../api';
import { useNotifications } from '../../context/NotificationContext';
import { useCampContext } from '../../context/CampContext';
import { formatDateToString } from '../../utils/dateUtils';
import CampSearchModal from './CampSearchModal';
import { Camp } from '../../utils/types';
import './CalendarEventPopup.css';

interface AddEventPopupProps {
  selectedDates: Date[];
  onClose: () => void;
  position: { top: number; left?: number; centered?: boolean };
  scheduleUuid: string | null;
}

const AddEventPopup: React.FC<AddEventPopupProps> = ({
  selectedDates,
  onClose,
  position,
  scheduleUuid,
}) => {
  const [title, setTitle] = useState('');
  const [camps, setCamps] = useState<Camp[]>([]);
  const [showCampsPopup, setShowCampsPopup] = useState(false);
  const [selectedDatesByCampId, setSelectedDatesByCampId] = useState<Record<string, string>>({});

  const handleCampDateChange = (campId: string, dateId: string) => {
    setSelectedDatesByCampId(prev => ({ ...prev, [campId]: dateId }));
  };
  const createCustomEventMutation = useCreateCustomEvent();
  const filterCampsMutation = useFilterCamps();
  const { showSuccess, showError } = useNotifications();
  const { schedule, setSchedule } = useCampContext();
  const navigate = useNavigate();

  const handleSearchCamps = () => {
    if (selectedDates.length > 0) {
      const sortedDates = [...selectedDates].sort((a, b) => a.getTime() - b.getTime());
      const startDate = formatDateToString(sortedDates[0]);
      const endDate = formatDateToString(sortedDates[sortedDates.length - 1]);

      const rangeStart = sortedDates[0];
      const rangeEnd = sortedDates[sortedDates.length - 1];

      filterCampsMutation.mutate(
        { start_date: startDate, end_date: endDate },
        {
          onSuccess: (data) => {
            const camps = data.camps || [];
            setCamps(camps);

            // Auto-select first date within range for each camp
            const initialSelections: Record<string, string> = {};
            const rangeStartStr = formatDateToString(rangeStart);
            const rangeEndStr = formatDateToString(rangeEnd);

            camps.forEach(camp => {
              // Find first camp date that starts within the selected range (compare as strings to avoid timezone issues)
              const datesInRange = camp.camp_dates
                .filter(campDate => {
                  return campDate.start_date >= rangeStartStr && campDate.start_date <= rangeEndStr;
                })
                .sort((a, b) => a.start_date.localeCompare(b.start_date));

              if (datesInRange.length > 0) {
                initialSelections[camp.id] = datesInRange[0].id;
              }
            });

            setSelectedDatesByCampId(initialSelections);
            setShowCampsPopup(true);
          },
          onError: (error) => {
            console.error('Failed to fetch camps:', error);
            setCamps([]);
            setShowCampsPopup(true);
          },
        }
      );
    }
  };

  const getScheduledDateIds = (campId: string): string[] => {
    const scheduledDateIds: string[] = [];
    if (schedule?.scheduled_camps) {
      for (const scheduled of schedule.scheduled_camps) {
        if (scheduled.camp.id === campId) {
          scheduledDateIds.push(scheduled.camp_date.id);
        }
      }
    }
    return scheduledDateIds;
  };

  const handleOpenInSearch = () => {
    const sortedDates = [...selectedDates].sort((a, b) => a.getTime() - b.getTime());
    const startDate = formatDateToString(sortedDates[0]);
    const endDate = formatDateToString(sortedDates[sortedDates.length - 1]);
    navigate(`/search?start_date=${startDate}&end_date=${endDate}`);
    onClose();
  };

  const handleSave = async () => {
    if (!title.trim()) {
      showError('Please enter a title for the event', new Error('Title is required'));
      return;
    }

    if (selectedDates.length === 0) {
      showError('No dates selected', new Error('Dates are required'));
      return;
    }

    if (!scheduleUuid) {
      showError('No schedule selected', new Error('Schedule is required'));
      return;
    }

    // Sort dates to get start and end
    const sortedDates = [...selectedDates].sort((a, b) => a.getTime() - b.getTime());
    const startDate = formatDateToString(sortedDates[0]);
    const endDate = formatDateToString(sortedDates[sortedDates.length - 1]);

    createCustomEventMutation.mutate(
      { scheduleUuid: scheduleUuid, title: title.trim(), startDate, endDate },
      {
        onSuccess: (data) => {
          showSuccess('Event created successfully');
          console.log('Created event data:', data);
          schedule?.custom_events?.push(data);
          setSchedule(schedule);
          onClose();
        },
        onError: (error) => {
          showError('Failed to create event. Please try again.', error);
        },
      }
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  const handleCloseCampSearchModal = () => {
    setShowCampsPopup(false);
    onClose();
  };

  return (
    <div
      className={`add-event-popup ${position.centered ? 'popup-centered' : ''}`}
      style={{
        top: `${position.top}px`,
        left: position.left !== undefined ? `${position.left}px` : undefined
      }}
    >
      <div className="calendar-popup-header">
        <button className="calendar-popup-close" onClick={onClose} aria-label="Close">
          <X className="icon icon--sm" />
        </button>
      </div>

      <div className="calendar-popup-content">
        <div className="calendar-popup-input-container">
          <input
            type="text"
            className="calendar-popup-title-input"
            placeholder="Add title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyPress}
            autoFocus
            maxLength={100}
          />
        </div>

        {selectedDates.length > 0 && (
          <div className="calendar-popup-date-display">
            <Clock className="calendar-popup-date-icon" size={16} />
            <span className="calendar-popup-date-text">
              {selectedDates.length === 1
                ? selectedDates[0].toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                : `${selectedDates.sort((a, b) => a.getTime() - b.getTime())[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric'})} – ${selectedDates.sort((a, b) => a.getTime() - b.getTime())[selectedDates.length - 1].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric'})}`
              }
            </span>
          </div>
        )}

        <button
          className="calendar-popup-search-link"
          onClick={handleSearchCamps}
          disabled={filterCampsMutation.isPending}
        >
          <Search className="calendar-popup-search-icon" size={16} />
          <span>
            {filterCampsMutation.isPending
              ? 'Loading...'
              : selectedDates.length === 1
                ? 'Show camps for this date.'
                : 'Show camps for these dates.'
            }
          </span>
        </button>

        <div className="add-event-actions">
          <button
            className="btn btn--primary btn--sm"
            onClick={handleSave}
            disabled={!title.trim() || createCustomEventMutation.isPending}
          >
            {createCustomEventMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {showCampsPopup && (
        <CampSearchModal
          camps={camps}
          selectedDates={selectedDates}
          selectedDatesByCampId={selectedDatesByCampId}
          onDateChange={handleCampDateChange}
          getScheduledDateIds={getScheduledDateIds}
          onClose={handleCloseCampSearchModal}
          onOpenInSearch={handleOpenInSearch}
        />
      )}
    </div>
  );
};

export default AddEventPopup;
